import { useAiChatActions } from '@euler/app/flows/ai-suite/chat/AiChatActions';
import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import {
  PrimaryButton,
  SecondaryButton,
} from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiAnswer, BiAnswerAction, biAnswerText } from '@euler/model/bi/answer';
import { BiChangeSetDetail } from '@euler/model/bi/change-set';
import { Check, Eye, LayoutDashboard, RotateCcw, X } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import { View } from 'react-native';
import { filter } from 'rxjs';
import { useOpenBiDashboard } from '../bi-visualization/functions/useOpenBiDashboard';
import { useBiDashboardHost } from './BiDashboardHostContext';
import { BiChangePhase } from './functions/biChangePhase';

const kNotices: Record<
  Exclude<BiChangePhase, BiChangePhase.Pending>,
  string
> = {
  [BiChangePhase.Applied]: '这些修改已应用到看板。',
  [BiChangePhase.Discarded]: '这些修改已放弃。',
  [BiChangePhase.Stale]: '看板在这之后被改过了，这些修改没有应用。',
  [BiChangePhase.Expired]: '这些修改超过 24 小时没有确认，已失效。',
};

/**
 * What the card offers by phase: 应用 / 放弃 while pending; for a stale
 * or expired set, asking again against the dashboard as it is now.
 */
export const ChangeActions = memo(
  ({
    detail,
    phase,
    attached,
    onHost,
  }: {
    detail: BiChangeSetDetail;
    phase: BiChangePhase;
    /** In a conversation a tap can be sent in. */
    attached: boolean;
    /** The chat is open on this change set's dashboard. */
    onHost: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const chat = useAiChatActions();
    const host = useBiDashboardHost();
    const open = useOpenBiDashboard();
    const [sent, setSent] = useState<BiAnswerAction | null>(null);
    const { changeSet, dashboard } = detail;
    const { tapSettled$ } = chat;
    // Once the tap settles, the phase tells the outcome.
    useEffect(() => {
      const subscription = tapSettled$
        .pipe(
          filter(
            tap =>
              tap.kind === 'bi' &&
              (tap.answer.action === BiAnswerAction.ApplyChanges ||
                tap.answer.action === BiAnswerAction.DiscardChanges) &&
              tap.answer.changeSetId === changeSet.id,
          ),
        )
        .subscribe(() => setSent(null));
      return () => subscription.unsubscribe();
    }, [tapSettled$, changeSet.id]);
    const tap = (answer: BiAnswer) => {
      if (chat.send(biAnswerText(answer), { biAnswer: answer }))
        setSent(answer.action);
    };
    const reveal = onHost ? host?.reveal ?? null : null;
    const openDashboard = onHost
      ? null
      : () => open(dashboard.id, dashboard.name);

    if (phase === BiChangePhase.Pending) {
      if (!attached)
        return openDashboard ? (
          <SecondaryButton
            title="打开看板"
            icon={LayoutDashboard}
            onPress={openDashboard}
          />
        ) : null;
      const disabled = chat.busy || sent != null;
      return (
        <View style={{ gap: rpx(8) }}>
          <View style={{ flexDirection: 'row', gap: rpx(8) }}>
            <View style={{ flex: 1 }}>
              <PrimaryButton
                title="应用"
                icon={Check}
                loading={sent === BiAnswerAction.ApplyChanges}
                disabled={disabled}
                onPress={() =>
                  tap({
                    action: BiAnswerAction.ApplyChanges,
                    changeSetId: changeSet.id,
                  })
                }
              />
            </View>
            <SecondaryButton
              title="放弃"
              icon={X}
              loading={sent === BiAnswerAction.DiscardChanges}
              disabled={disabled}
              onPress={() =>
                tap({
                  action: BiAnswerAction.DiscardChanges,
                  changeSetId: changeSet.id,
                })
              }
            />
          </View>
          {reveal ? (
            <SecondaryButton title="在看板上预览" icon={Eye} onPress={reveal} />
          ) : openDashboard ? (
            <SecondaryButton
              title="打开看板"
              icon={LayoutDashboard}
              onPress={openDashboard}
            />
          ) : null}
        </View>
      );
    }
    const askAgain =
      attached &&
      (phase === BiChangePhase.Stale || phase === BiChangePhase.Expired);
    return (
      <View style={{ gap: rpx(8) }}>
        <CardNotice text={kNotices[phase]} />
        {askAgain ? (
          <SecondaryButton
            title="按现在的看板重新生成"
            icon={RotateCcw}
            disabled={chat.busy}
            onPress={() =>
              chat.send(
                `看板已经变了，请按现在的看板重新给出这些修改：${changeSet.summary}`,
              )
            }
          />
        ) : null}
      </View>
    );
  },
);
